/**
 * CONTEXT MODE discussion (MODES.md §3, M4) — pure helpers for the room's discussion feed.
 * The GET list is the truth; live ``CONTEXT_MESSAGE`` beacons on the room channel append to it.
 */

/** The discussion messages carried by live channel envelopes (``CONTEXT_MESSAGE``). Pure. */
export function liveDiscussionFrom(channelMessages = []) {
  const out = [];
  for (const e of channelMessages || []) {
    if (!e || (e.eventType ?? e.type) !== 'CONTEXT_MESSAGE') continue;
    const m = e.payload && e.payload.message;
    if (m && m.id) out.push(m);
  }
  return out;
}

/** Merge messages (dedupe by id), oldest first by their sortable key. An incoming copy of a message we already
 *  have REPLACES it when it changed (an adoption — MODES.md §10.4; an edit or a delete — §3). A stale copy (an
 *  older edit replayed from the live buffer) never undoes a newer one, and nothing un-deletes. Returns the SAME array when nothing new or
 *  changed arrived (so a re-render doesn't churn). Pure. */
export function mergeDiscussion(current = [], incoming = []) {
  const byId = new Map((current || []).map((m) => [m.id, m]));
  let changed = false;
  for (const m of incoming || []) {
    if (!m || !m.id) continue;
    const had = byId.get(m.id);
    if (!had) { byId.set(m.id, m); changed = true; continue; }
    if (had.deleted) continue;                                         // nothing un-deletes
    // a message's REVISION = its latest edit / signal stamp — a stale replay (older rev) never undoes a newer one
    const rev = (x) => [x.editedAt || '', x.signaledAt || '', x.updatedAt || ''].sort().pop();
    const newer = m.deleted || rev(m) > rev(had)
      || (rev(m) === rev(had) && (m.adoptedBy || '') !== (had.adoptedBy || ''));
    if (newer) { byId.set(m.id, { ...had, ...m }); changed = true; }
  }
  if (!changed) return current || [];
  const key = (m) => String(m.sk || m.at || '');
  return [...byId.values()].sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
}

/** THE ROOM'S ACTIVITY (owner 2026-10-03 — "anything is an activity"): one chronological stream like Slack, with
 *  agents as members — network run blocks, direct-run cards and discussion messages merged by time. A direct
 *  run's own question / answer messages (``runId``) are left to its card (shown once). Items without a time keep
 *  their relative order. Pure. */
export function streamTimeline(runBlocks = [], directRuns = [], messages = []) {
  const items = [];
  let seq = 0;
  const push = (kind, ts, item) => items.push({ kind, ts: String(ts || ''), seq: seq++, item });
  for (const rb of runBlocks || []) push('run', ((rb.turns || [])[0] || {}).startedAt, rb);
  for (const d of directRuns || []) push('direct', d.startedAt || '', d);
  for (const m of messages || []) if (m && !m.runId) push('message', m.at, m);
  // stable: by time where both have one, else keep insertion order
  return items.sort((a, b) => ((a.ts && b.ts && a.ts !== b.ts) ? (a.ts < b.ts ? -1 : 1) : a.seq - b.seq));
}

/** DAY GROUPS (Slack-style, MODES.md §3) — the stream's group label for a timestamp, by calendar recency:
 *  Today · Yesterday · a weekday this week ("Monday") · Last week · Earlier this month · Last month · a month this
 *  year ("August") · an older month ("August 2025"). Weeks start Monday. Buckets nest by recency, so in a
 *  chronological stream the labels never go backwards. '' for a missing/bad time. Pure. */
export function streamGroupLabel(ts, now = new Date()) {
  if (!ts) return '';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const day = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const today = day(now);
  const that = day(d);
  const daysAgo = Math.round((today - that) / 86400000);
  // SLACK (MODES.md §3a-groups): ONE header per DAY — Today · Yesterday · "Thursday, August 20th" (+ the year when it
  // isn't this year)
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  const n = d.getDate();
  const ord = (n % 100 >= 11 && n % 100 <= 13) ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] || 'th');
  const base = `${d.toLocaleDateString('en-US', { weekday: 'long' })}, ${d.toLocaleDateString('en-US', { month: 'long' })} ${n}${ord}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base}, ${d.getFullYear()}`;
}

/** Does this group label name ONE day? Every header is one day now (Slack) → a message under it shows only its clock
 *  time. An untimed group ('') keeps the full stamp. */
export function isSingleDayGroup(label) {
  return !!label;
}

/** ``streamTimeline`` items with a ``{kind:'divider', label}`` row before each new group (an item with no time stays
 *  in the group above it); each item is tagged ``group`` = the label it sits under. Pure (items are copied). */
export function withStreamGroups(items = [], now = new Date()) {
  const out = [];
  let current = '';
  for (const it of items || []) {
    const label = streamGroupLabel(it && it.ts, now);
    if (label && label !== current) {
      current = label;
      out.push({ kind: 'divider', label, key: `divider:${label}:${out.length}`, ts: it.ts });
    }
    out.push(it && typeof it === 'object' ? { ...it, group: current } : it);
  }
  return out;
}

/** ROSTER ROLL-UP (ROOMS.md §roster-lines, Slack's "along with 2 others"): consecutive JOIN lines (added / rejoined)
 *  within ``GROUP_GAP_MS`` become ONE line carrying ``rollup`` (every join, oldest first). Pure. */
export function withRosterRollups(items = []) {
  const out = [];
  for (const it of items || []) {
    const m = it && it.kind === 'message' ? it.item : null;
    const ev = m && m.kind === 'system' ? (m.system || {}).event : '';
    const last = out[out.length - 1];
    const lm = last && last.kind === 'message' ? last.item : null;
    if (ev && (ev === 'added' || ev === 'rejoined') && lm && lm.kind === 'system'
        && ['added', 'rejoined'].includes((lm.system || {}).event)) {
      const gap = Date.parse(m.at || '') - Date.parse((lm.rollup ? lm.rollup[lm.rollup.length - 1] : lm).at || '');
      if (Number.isFinite(gap) && gap >= 0 && gap <= GROUP_GAP_MS) {
        out[out.length - 1] = { ...last, item: { ...lm, rollup: [...(lm.rollup || [lm]), m] } };
        continue;
      }
    }
    out.push(it);
  }
  return out;
}

/** SLACK GROUPING (MODES.md §3f) — a message right after one from the SAME author (the same person, or the same agent)
 *  within ``GROUP_GAP_MS`` and the same day group is a CONTINUATION: no avatar, no name row — just the text (the time
 *  shows on hover in the gutter). A message carrying a label that matters (pending / on behalf of / adopted / mentions
 *  me / agents it asked) always keeps its header. Pure: tags ``compact: true``. */
export const GROUP_GAP_MS = 5 * 60 * 1000;
function _authorKey(m) {
  if (!m) return '';
  if (m.kind === 'agent') return `agent:${m.agentId || m.agentName || ''}`;
  if (m.kind === 'user') return `user:${m.authorSub || m.authorName || ''}`;
  return '';
}
function _hasHeaderLabel(m, me) {
  return !!(m.pending || m.standin || m.adoptedBy || m.agentId && m.kind === 'user'
    || (me && (m.mentions || []).includes(me)));
}
export function withContinuations(items = [], me = '') {
  const out = [];
  let prev = null;
  for (const it of items || []) {
    const m = it && it.kind === 'message' ? it.item : null;
    let compact = false;
    if (m && prev && !m.deleted && !prev.deleted && _authorKey(m) && _authorKey(m) === _authorKey(prev)
        && !_hasHeaderLabel(m, me)) {
      const gap = Date.parse(m.at || '') - Date.parse(prev.at || '');
      compact = Number.isFinite(gap) && gap >= 0 && gap <= GROUP_GAP_MS;
    }
    out.push(m ? { ...it, compact } : it);
    prev = m && m.kind !== 'context' ? m : null;      // a divider / run / card breaks the group
  }
  return out;
}

/** THREADS (MODES.md §3b, Slack-style, ONE level): split the discussion into the TOP-LEVEL stream (no ``threadOf``)
 *  and ``{rootId: [replies oldest-first]}``. A deleted reply disappears; a reply whose root isn't loaded waits. Pure. */
export function splitThreads(messages = []) {
  const top = [];
  const replies = {};
  for (const m of messages || []) {
    if (!m) continue;
    if (m.threadOf) {
      if (!m.deleted) (replies[m.threadOf] = replies[m.threadOf] || []).push(m);
    } else top.push(m);
  }
  return { top, replies };
}

/** THE CONVERSATION (MODES.md §10.2): an Ask is answered ASYNC — the question returns at once (``pending``) and each
 *  answer lands live. ``pending`` = {key: {agentName, agentId?, questionId?, since}} (key = ``question:agent``, or
 *  the bare question id). An answer (``replyTo`` = the question, from THAT agent) or a ``CONTEXT_ASK_FAILED`` beacon
 *  for it settles it; one older than ``maxAgeMs`` is dropped (the answer still shows when it lands).
 *  → {pending, errors[]} — the SAME ``pending`` object when nothing settled. Pure. */
export function settleAsks(pending = {}, messages = [], channel = [], now = Date.now(), maxAgeMs = 180000) {
  const keys = Object.keys(pending || {});
  if (!keys.length) return { pending: pending || {}, errors: [] };
  const answered = new Set();
  for (const m of messages || []) {
    if (!m || !m.replyTo) continue;
    answered.add(m.replyTo);
    if (m.agentId) answered.add(`${m.replyTo}:${m.agentId}`);
  }
  const failed = {};
  for (const e of channel || []) {
    if (!e || (e.eventType ?? e.type) !== 'CONTEXT_ASK_FAILED') continue;
    const q = e.payload && e.payload.questionId;
    const err = (e.payload && e.payload.error) || "The agent couldn't answer just now — ask again.";
    if (q) { failed[q] = err; if (e.payload.agentId) failed[`${q}:${e.payload.agentId}`] = err; }
  }
  const next = {};
  const errors = [];
  let changed = false;
  for (const k of keys) {
    const p = pending[k] || {};
    const key = p.questionId && p.agentId ? `${p.questionId}:${p.agentId}` : k;
    if (answered.has(key)) { changed = true; continue; }
    if (failed[key]) { changed = true; errors.push(failed[key]); continue; }
    if (now - (p.since || now) > maxAgeMs) { changed = true; continue; }
    next[k] = p;
  }
  return { pending: changed ? next : pending, errors };
}

/** "Architect, Security" — who is answering now. Pure. */
export function pendingAskNames(pending = {}) {
  return [...new Set(Object.values(pending || {}).map((p) => p && p.agentName).filter(Boolean))].join(', ');
}

/** @MENTIONS (MODES.md §10.6) — the agents a person's text names with ``@Name`` (or ``@id``), in order of first
 *  mention, deduped. Longest names match first (``@Platform Build`` beats ``@Platform``); a mention must end at a
 *  word boundary. ``agents`` = [{id, name}]. Pure. */
export function parseMentions(text = '', agents = []) {
  const s = String(text || '');
  const keys = [];
  for (const a of agents || []) {
    if (!a || !a.id) continue;
    for (const k of new Set([a.name, a.id].filter(Boolean))) keys.push({ id: a.id, k: String(k) });
  }
  keys.sort((x, y) => y.k.length - x.k.length);
  const hits = [];
  let i = s.indexOf('@');
  while (i !== -1) {
    const after = s.slice(i + 1);
    const m = keys.find(({ k }) => after.toLowerCase().startsWith(k.toLowerCase())
      && !/[A-Za-z0-9_]/.test(after.charAt(k.length)));
    if (m && (i === 0 || /\s|[(,]/.test(s.charAt(i - 1)))) hits.push({ at: i, id: m.id });
    i = s.indexOf('@', i + 1);
  }
  return [...new Set(hits.sort((a, b) => a.at - b.at).map((h) => h.id))];
}

/** SCREEN-SIZED PAGES (MODES.md §3, Slack) — how many messages fill ``heightPx`` of stream, plus a small buffer so the
 *  first scroll up never shows a blank edge: a compact message is ~44px. Clamped to [12, 50] (the server caps at 200).
 *  A laptop (~800px stream) → 25; a tall monitor (~1300px) → 36. Pure. */
export const MSG_ROW_PX = 44;
export function screenPage(heightPx = 0, { buffer = 6, min = 12, max = 50 } = {}) {
  const h = Number(heightPx) > 0 ? Number(heightPx) : 800;
  return Math.max(min, Math.min(max, Math.ceil(h / MSG_ROW_PX) + buffer));
}

/** MENTION TAGS in the composer (MENTIONS.md §1) — ``text`` split into plain runs and ``@Label`` runs for the labels
 *  picked (agents' names / ids, tagged people's names), so the field can draw each as a tag. Same rule as
 *  ``parseMentions``: longest label first, ``@`` at the start or after a space / ``(`` / ``,``, ends at a word boundary.
 *  → [{text, tag: false} | {text, tag: true, kind}] (concatenated = ``text``). Pure. */
export function mentionSegments(text = '', labels = []) {
  const s = String(text || '');
  const keys = (labels || []).filter((l) => l && l.label).map((l) => ({ k: String(l.label), kind: l.kind || 'agent' }))
    .sort((x, y) => y.k.length - x.k.length);
  const out = [];
  let plain = '';
  let i = 0;
  while (i < s.length) {
    if (s.charAt(i) === '@' && (i === 0 || /\s|[(,]/.test(s.charAt(i - 1)))) {
      const after = s.slice(i + 1);
      const m = keys.find(({ k }) => after.toLowerCase().startsWith(k.toLowerCase())
        && !/[A-Za-z0-9_]/.test(after.charAt(k.length)));
      if (m) {
        if (plain) out.push({ text: plain, tag: false });
        plain = '';
        out.push({ text: s.slice(i, i + 1 + m.k.length), tag: true, kind: m.kind });
        i += 1 + m.k.length;
        continue;
      }
    }
    plain += s.charAt(i);
    i += 1;
  }
  if (plain) out.push({ text: plain, tag: false });
  return out;
}

/** Where each tag sits in the text — ``mentionSegments`` → [{start, end, kind}] (end exclusive). Pure. */
export function mentionRanges(segments = []) {
  const out = [];
  let at = 0;
  for (const x of segments || []) {
    const len = String(x.text || '').length;
    if (x.tag) out.push({ start: at, end: at + len, kind: x.kind });
    at += len;
  }
  return out;
}

/** CHIP EDITING (MENTIONS.md §1 — the common chip pattern): a tag is ONE unit.
 *  ``Backspace`` with the caret at/inside a tag's end, or ``Delete`` at/inside its start, removes the WHOLE tag; a
 *  selection that cuts into a tag widens to cover it. → {text, caret} to apply, or null (not a tag edit — let the field
 *  handle the key). Pure. */
export function chipDelete(text = '', selStart = 0, selEnd = 0, key = 'Backspace', ranges = []) {
  const s = String(text || '');
  let a = Math.min(selStart, selEnd);
  let b = Math.max(selStart, selEnd);
  if (a === b) {
    const r = (ranges || []).find((x) => (key === 'Backspace' ? (a > x.start && a <= x.end) : (a >= x.start && a < x.end)));
    if (!r) return null;
    return { text: s.slice(0, r.start) + s.slice(r.end), caret: r.start };
  }
  const cut = (ranges || []).filter((x) => x.start < b && x.end > a);
  if (!cut.length) return null;
  for (const x of cut) { a = Math.min(a, x.start); b = Math.max(b, x.end); }
  return { text: s.slice(0, a) + s.slice(b), caret: a };
}

/** A click INSIDE a tag selects the whole tag (so Backspace / typing replaces it); a keyboard caret never rests
 *  inside one — it hops to the nearer edge in the direction it moved. → [start, end] or null. Pure. */
export function chipSelection(selStart = 0, selEnd = 0, ranges = [], { click = false, dir = 0 } = {}) {
  if (selStart !== selEnd) return null;
  const r = (ranges || []).find((x) => selStart > x.start && selStart < x.end);
  if (!r) return null;
  if (click) return [r.start, r.end];
  const at = dir < 0 ? r.start : dir > 0 ? r.end : (selStart - r.start < r.end - selStart ? r.start : r.end);
  return [at, at];
}

/** The partial ``@que`` the caret is in (for the mention picker) → {query, start} or null. Pure. */
export function mentionAt(text = '', caret = 0) {
  const before = String(text || '').slice(0, Math.max(0, caret));
  const m = /(^|\s)@([^\s@]{0,40})$/.exec(before);
  return m ? { query: m[2], start: before.length - m[2].length - 1 } : null;
}

/** ASK PROGRESS (MODES.md §10.2a) — what each agent is doing on each question RIGHT NOW, for everyone in the room, from
 *  the live channel: ``CONTEXT_AGENT_STATUS`` (phase) + ``CONTEXT_AGENT_DELTA`` (the answer so far — cumulative, the
 *  highest ``seq`` wins, so a dropped event loses nothing). ``local`` = the asker's own just-sent asks (shown at once,
 *  before the first beacon). An entry ends when its stored answer is in ``messages`` (replyTo = the question, from that
 *  agent), on ``done`` / ``failed``, or after ``maxAgeMs``.
 *  → [{key, questionId, agentId, agentName, phase, label, text, since}] oldest first. Pure. */
export function liveAskProgress(channel = [], messages = [], local = {}, now = Date.now(), maxAgeMs = 180000) {
  const by = new Map();
  const at = (iso) => { const t = Date.parse(iso || ''); return Number.isFinite(t) ? t : now; };
  const get = (q, a, name) => {
    const k = `${q}:${a}`;
    if (!by.has(k)) by.set(k, { key: k, questionId: q, agentId: a, agentName: name || a, phase: 'queued', label: '',
      text: '', seq: 0, since: now, ended: false });
    const e = by.get(k);
    if (name) e.agentName = name;
    return e;
  };
  for (const p of Object.values(local || {})) {
    if (p && p.questionId && p.agentId) {
      const e = get(p.questionId, p.agentId, p.agentName);
      e.since = Math.min(e.since, p.since || now);
    }
  }
  for (const ev of channel || []) {
    const t = ev && (ev.eventType ?? ev.type);
    if (t !== 'CONTEXT_AGENT_STATUS' && t !== 'CONTEXT_AGENT_DELTA') continue;
    const pl = (ev && ev.payload) || {};
    if (!pl.questionId || !pl.agentId) continue;
    const e = get(pl.questionId, pl.agentId, pl.agentName);
    if (t === 'CONTEXT_AGENT_STATUS') {
      if (pl.phase === 'done' || pl.phase === 'failed') e.ended = true;
      else {
        // the latest STEP is remembered even while text shows — a cleared preamble falls back to it (§10.9)
        if (pl.phase !== 'writing') { e.step = pl.phase; e.stepLabel = pl.label || ''; }
        if (pl.web) e.web = pl.web;                   // the LIVE WEB picture (cumulative — the newest wins)
        if (!(e.phase === 'writing' && e.text && pl.phase !== 'writing')) { e.phase = pl.phase || e.phase; e.label = pl.label || ''; }
      }
      if (pl.phase === 'queued' || pl.phase === 'reading') e.since = Math.min(e.since, at(pl.at));
    } else if (Number(pl.seq || 0) > e.seq) {
      e.seq = Number(pl.seq || 0); e.text = String(pl.text || '');
      // an EMPTY newer text = a tool-only turn's preamble dropped → back to the step it is on (e.g. searching)
      if (e.text) { e.phase = 'writing'; e.label = 'Writing'; } else { e.phase = e.step || 'thinking'; e.label = e.stepLabel || ''; }
    }
  }
  const answered = new Set();
  for (const m of messages || []) if (m && m.replyTo && m.agentId) answered.add(`${m.replyTo}:${m.agentId}`);
  return [...by.values()]
    .filter((e) => !e.ended && !answered.has(e.key) && now - e.since <= maxAgeMs)
    .sort((a, b) => a.since - b.since);
}

/** A just-sent message shown AT ONCE (before the server answers) — replaced by the real one. Pure. */
export function optimisticMessage({ text, me, meName = '', agentId = '', agentName = '', now = Date.now() }) {
  return { id: `local-${now}`, local: true, kind: 'user', text, authorSub: me, authorName: meName, agentId,
    agentName, at: new Date(now).toISOString() };
}

/** Drop optimistic messages the server has now returned / echoed live (same author + text). Pure. */
export function withoutEchoedLocals(messages = []) {
  const real = new Set((messages || []).filter((m) => m && !m.local && m.kind === 'user').map((m) => `${m.authorSub}|${m.text}`));
  const out = (messages || []).filter((m) => !(m && m.local && real.has(`${m.authorSub}|${m.text}`)));
  return out.length === (messages || []).length ? messages : out;
}

/** SLACK DAY SECTIONS (MODES.md §3a-groups) — the stream cut into one section per day: a day's divider + everything
 *  until the next divider (items before the first divider form a leading section). Each section owns its sticky
 *  pill, so only the day you're reading shows — pills never stack. → [{key, items}]. Pure. */
export function streamDaySections(stream = []) {
  const out = [];
  let cur = null;
  for (const it of stream || []) {
    if (it && it.kind === 'divider') { cur = { key: `day:${it.key}`, items: [it] }; out.push(cur); continue; }
    if (!cur) { cur = { key: 'day:lead', items: [] }; out.push(cur); }
    cur.items.push(it);
  }
  return out;
}

/** What a message may be kept as here (KNOWLEDGE.md §14): any room a document; an expert's room also a procedure; a
 *  trained answer only in a TEAM's room — it is that Team's (a Lead room answers for all of them; L238). The server
 *  checks it again. */
export function keepKindsFor(rm = {}) {
  const expertRoom = rm.expertPart || rm.expertTraining || (rm.expertConsults?.teams || []).length;
  if (!expertRoom) return ['document'];
  return rm.expertPart?.team ? ['document', 'procedure', 'qa'] : ['document', 'procedure'];
}
